import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Info, RefreshCw } from "lucide-react";
import api, { relTime } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ChannelIcon from "@/components/ChannelIcon";

const STEPS = [
  "In Google Ads, open Assets → Lead forms and edit (or create) your lead form.",
  "Scroll to “Lead delivery options” → Webhook integration.",
  "Paste the Webhook URL and Key below, then click “Send test data”.",
  "Each submission lands in Inbox as a “Google Lead” ticket with a new Contact and a Lead in the queue.",
];

function CopyRow({ label, value, testId, copy }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        <Input readOnly value={value || ""} data-testid={testId} className="rounded-sm bg-secondary/40 font-mono text-xs" />
        <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => copy(value || "")} data-testid={`${testId}-copy`}><Copy className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
}

export const GoogleLeadFormsCard = ({ copy }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["google-leads-info"], queryFn: () => api.get("/integrations/google-leads").then((r) => r.data) });
  const rotate = useMutation({
    mutationFn: () => api.post("/integrations/google-leads/rotate").then((r) => r.data),
    onSuccess: (d) => { qc.setQueryData(["google-leads-info"], d); toast.success("New webhook URL & key generated — update Google Ads"); },
    onError: () => toast.error("Could not rotate key"),
  });
  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="google-leads-card">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="flex items-center gap-3">
          <ChannelIcon channel="google_lead" size={18} badge />
          <div>
            <div className="font-display text-xl font-extrabold">Google Ads Lead Forms</div>
            <div className="text-xs text-muted-foreground">Webhook for division <strong>{data?.division_name || "…"}</strong>. Each division has its own URL &amp; key.</div>
          </div>
        </div>
        <Badge variant="outline" className="rounded-sm text-[10px]" data-testid="google-leads-count">{data?.leads_count ?? 0} leads{data?.last_lead_at ? ` · last ${relTime(data.last_lead_at)}` : ""}</Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <CopyRow label="Webhook URL" value={data?.webhook_url} testId="google-leads-webhook-url" copy={copy} />
        <CopyRow label="Key (google_key)" value={data?.google_key} testId="google-leads-key" copy={copy} />
      </div>
      <div className="mt-6 p-4 rounded-sm bg-secondary/40 border border-border">
        <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Info className="h-4 w-4 text-primary" /> Setup in Google Ads</div>
        <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal ml-5">{STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
      </div>
      <div className="mt-4 flex justify-end">
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => rotate.mutate()} disabled={rotate.isPending} data-testid="google-leads-rotate">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Regenerate URL &amp; key
        </Button>
      </div>
    </Card>
  );
};
